import { Alert, Button, Card, Col, Form, Input, Modal, Row, Select, Space, Spin, Typography } from "antd";
import { useContext, useEffect, useState } from "react";
import ApiContext from "../api/context";
import { SubmitStatus } from "../api/work-order-api";
import { LoadingOutlined } from '@ant-design/icons';
import { titleStyle } from "../constants/style";
import Menu from "./master/menu";
import { toDateString } from "../constants";
import Paragraph from "antd/es/typography/Paragraph";
import { User } from "../api/models";
const { Text, Title } = Typography;

interface PasswordManagementParams {
  user?: User
}


function PasswordManagement({ user }: PasswordManagementParams) {
  const { api } = useContext(ApiContext);
  const [error, setError] = useState<String | undefined>();
  const [submitStatus, setSubmitStatus] = useState(SubmitStatus.Idle);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmationPassword, setConfirmationPassword] = useState("");

  const isValidPassword = (p: string) => p.length > 3;
  const hasCurrentPassword = isValidPassword(currentPassword);
  const hasNewPassword = isValidPassword(newPassword);
  const hasConfirmationPassword = isValidPassword(confirmationPassword);
  const hasMatchingPasswords = hasConfirmationPassword &&
    hasNewPassword && newPassword === confirmationPassword;

  const passwordChanged = submitStatus === SubmitStatus.Submitted;
  const canSubmit = !passwordChanged &&
    hasMatchingPasswords &&
    submitStatus !== SubmitStatus.Submitting;

  return (
    <Card>
      <Spin spinning={!user}>
        <Row justify="space-between">
          <Col span={12}>
            <Paragraph>
              <Text strong>Email Address</Text>
              <br />
              <Text>{user?.emailAddress}</Text>
            </Paragraph>
          </Col>
          <Col span={12}>
            <Paragraph>
              <Text strong>Account Creation Date</Text>
              <br />
              <Text>{toDateString(user?.creationTime)}</Text>
            </Paragraph>
          </Col>
        </Row>
        <Row justify="end">
          <Col span={12}>
            <Paragraph>
              <Text strong>Password Last Changed Date</Text>
              <br />
              <Text>{toDateString(user?.lastPasswordSetTime)}</Text>
            </Paragraph>
          </Col>
          <Col span={12}>
          </Col>
        </Row>
        <Row justify="end">
          <Button onClick={() => setIsChangingPassword(true)}>
            Change Password
          </Button>
        </Row>
        <Paragraph />
        <Modal
          open={isChangingPassword}
          closable={false}
          width={350}
          footer={<></>}>
          <Form
            layout="vertical"
            onFinish={() => {
              setSubmitStatus(SubmitStatus.Submitting);
              api?.putSelfPassword(currentPassword, newPassword).then(response => {
                if (!response.good()) {
                  switch (response.status) {
                    case 400:
                      setError("The new password provided is invalid.");
                      break;
                    case 401:
                      setError("Either the current password provided is invalid or your session has expired.");
                      break;
                    default:
                      setError("There was a problem processing the password change request.");
                      break;
                  }

                  setSubmitStatus(SubmitStatus.Failed);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmationPassword("");
                  return;
                }

                setSubmitStatus(SubmitStatus.Submitted);
              }).catch(err => {
                setSubmitStatus(SubmitStatus.Failed);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmationPassword("");
              });
            }}
          >
            <Form.Item label="Current Password">
              <Input
                placeholder="Confirm your current password"
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
              />
            </Form.Item>
            <Form.Item label="New Password">
              <Input
                placeholder="Enter your new password"
                type="password"
                value={newPassword}
                disabled={!hasCurrentPassword}
                onChange={e => {
                  setNewPassword(e.target.value);
                  setConfirmationPassword("");
                }}
              />
            </Form.Item>
            <Form.Item label="Confirm New Password">
              <Input
                placeholder="Confirm your new password"
                type="password"
                value={confirmationPassword}
                disabled={!hasNewPassword}
                onChange={e => setConfirmationPassword(e.target.value)}
              />
            </Form.Item>
            <>
              <Row justify="space-between">
                <Space>
                  <Spin
                    spinning={submitStatus === SubmitStatus.Submitting}
                    indicator={<LoadingOutlined spin />}
                  />
                </Space>
                <Space>
                  <Button
                    onClick={() => {
                      setIsChangingPassword(false);
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmationPassword("");
                      setSubmitStatus(SubmitStatus.Idle);
                    }}
                  >Close</Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={!canSubmit}>Change Password</Button>
                </Space>
              </Row>
              <Paragraph />
              {
                submitStatus !== SubmitStatus.Submitting ? undefined :
                  <Alert
                    type="info"
                    style={{ textAlign: "left" }}
                    message="Changing passsword." />
              }
              {
                !passwordChanged ? undefined :
                  <Alert
                    type="success"
                    style={{ textAlign: "left" }}
                    message="Password change successful." />
              }
              {
                submitStatus !== SubmitStatus.Failed ? undefined :
                  <Alert
                    type="error"
                    style={{ textAlign: "left" }}
                    message={error ?? "Password change failed."} />
              }
            </>
          </Form>
        </Modal>
      </Spin>
    </Card>
  );
}

export default function () {
  const { api } = useContext(ApiContext);
  const [user, setUser] = useState<User | undefined>(undefined);

  useEffect(() => {
    api?.getSelf().then(response => {
      setUser(response.body);
    }).catch(() => {
      setUser(undefined);
    });
  }, []);

  return (
    <Menu>
      <Title style={titleStyle} level={3}>Account</Title>
      <PasswordManagement user={user} />
    </Menu>
  );
}